import React from 'react';

// Hand-built time-of-day icon set for the dashboard greeting — sunrise, sun
// peeking out from behind a cloud, a sunset skyline, and a moon-and-stars
// night sky — drawn in the app's own teal/coral palette (#126e7d / #00bda5 /
// #fb8c5f) instead of stock emoji. `welcome` covers the very-early/late hours
// the greeting text falls back to "Welcome" for, so it gets the night icon
// rather than an unrelated wave — sunrise → midday → sunset → night reads as
// one coherent day-cycle family. Animation (rotation/drift/glow/twinkle) is
// applied purely via CSS classes in dashboard.css, gated behind
// prefers-reduced-motion.
const GreetingIcon = ({ type }) => {
  switch (type) {
    case 'morning':
      return (
        <svg className="greeting-icon-svg" width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
          <defs>
            <radialGradient id="gi-sun-morning" cx="35%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#ffc79a" />
              <stop offset="100%" stopColor="#fb8c5f" />
            </radialGradient>
          </defs>
          <g className="gi-rays" stroke="#fb8c5f" strokeWidth="1.6" strokeLinecap="round">
            <line x1="14" y1="1.5" x2="14" y2="5" />
            <line x1="14" y1="23" x2="14" y2="26.5" />
            <line x1="1.5" y1="14" x2="5" y2="14" />
            <line x1="23" y1="14" x2="26.5" y2="14" />
            <line x1="5.16" y1="5.16" x2="7.64" y2="7.64" />
            <line x1="20.36" y1="20.36" x2="22.84" y2="22.84" />
            <line x1="22.84" y1="5.16" x2="20.36" y2="7.64" />
            <line x1="7.64" y1="20.36" x2="5.16" y2="22.84" />
          </g>
          <circle className="gi-core" cx="14" cy="14" r="6.5" fill="url(#gi-sun-morning)" />
        </svg>
      );

    case 'afternoon':
      return (
        <svg className="greeting-icon-svg" width="32" height="28" viewBox="0 0 32 28" fill="none" aria-hidden="true">
          <defs>
            <radialGradient id="gi-sun-noon" cx="35%" cy="35%" r="70%">
              <stop offset="0%" stopColor="#ffc79a" />
              <stop offset="100%" stopColor="#fb8c5f" />
            </radialGradient>
            <linearGradient id="gi-cloud-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffffff" />
              <stop offset="100%" stopColor="#dff5f2" />
            </linearGradient>
          </defs>
          {/* Sun sits behind the cloud group below, drawn first so the cloud overlaps it */}
          <g>
            <g className="gi-rays-sm" stroke="#fb8c5f" strokeWidth="1.4" strokeLinecap="round">
              <line x1="22" y1="1.5" x2="22" y2="4" />
              <line x1="28.7" y1="4.5" x2="26.8" y2="6" />
              <line x1="15.3" y1="4.5" x2="17.2" y2="6" />
            </g>
            <circle className="gi-core" cx="22" cy="10" r="6" fill="url(#gi-sun-noon)" />
          </g>
          <g className="gi-cloud">
            <ellipse cx="8.5" cy="19" rx="6.2" ry="4.8" fill="url(#gi-cloud-fill)" />
            <ellipse cx="14.5" cy="15.5" rx="7" ry="6" fill="url(#gi-cloud-fill)" />
            <ellipse cx="20.5" cy="19" rx="5.2" ry="4.3" fill="url(#gi-cloud-fill)" />
            <rect x="3" y="17" width="21" height="7" rx="3.5" fill="url(#gi-cloud-fill)" />
          </g>
        </svg>
      );

    case 'evening':
      return (
        <svg className="greeting-icon-svg" width="32" height="26" viewBox="0 0 32 26" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="gi-dusk" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ffc79a" />
              <stop offset="55%" stopColor="#fb8c5f" />
              <stop offset="100%" stopColor="#126e7d" />
            </linearGradient>
          </defs>
          <g className="gi-stars" fill="#fb8c5f">
            <circle cx="4.5" cy="4" r="0.9" />
            <circle cx="27.5" cy="3" r="0.7" />
            <circle cx="24" cy="8" r="0.6" />
          </g>
          <circle className="gi-dusk-sun" cx="16" cy="17" r="7" fill="url(#gi-dusk)" />
          <rect x="0" y="19" width="32" height="7" fill="#0d4750" />
          <g className="gi-skyline" fill="#0a3d45">
            <rect x="2" y="15" width="3.4" height="11" />
            <rect x="7" y="18.5" width="2.6" height="7.5" />
            <rect x="11" y="12" width="3.6" height="14" />
            <rect x="17.5" y="17" width="2.8" height="9" />
            <rect x="21.5" y="14" width="3.2" height="12" />
            <rect x="26" y="19" width="3" height="7" />
          </g>
        </svg>
      );

    case 'welcome':
    default:
      return (
        <svg className="greeting-icon-svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <defs>
            <linearGradient id="gi-moon-fill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#dff5f2" />
              <stop offset="100%" stopColor="#00bda5" />
            </linearGradient>
          </defs>
          <g className="gi-stars" fill="#fb8c5f">
            <circle cx="4" cy="6" r="0.9" />
            <circle cx="19.5" cy="16" r="0.7" />
            <circle cx="3.5" cy="16.5" r="0.6" />
          </g>
          <path
            className="gi-moon"
            d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
            fill="url(#gi-moon-fill)"
          />
        </svg>
      );
  }
};

export default GreetingIcon;
